import { Component, createRef } from 'react';
import type { RefObject } from 'react';
import track from 'react-tracking';
import { Modal } from '@simplywallst/ui-core';
import { supportedCurrencies } from '@/constants/currencies';
import { Textfield } from '@simplywallst/ui-core';
import { Dropdown } from '@components/Dropdown';
import { validateExistingPortfolioName } from '@utilities/validation';
import { FieldWrapper, ServerNotification } from './styled';
import type {
  EditPortfolioPayload,
  EditPortfolioModalStatus,
} from '@/components/DeprecatedPortfolio/components/EditPortfolioModal/redux/interface';
import type { PortfolioItemState } from '@/components/DeprecatedPortfolios/redux/reducers/interface';

export interface EditPortfolioModalProps {
  error?: string;
  isUpdating: boolean;
  currencyISO: string;
  portfolioId: string;
  portfolioName: string;
  portfolioKeys: string[];
  portfolioEntities: Record<string, PortfolioItemState>;
  editPortfolioModalStatus: EditPortfolioModalStatus;
  onSubmit: (data: EditPortfolioPayload) => void;
  onClose: () => void;
}

interface Validation {
  error: string;
  dirty: boolean;
}

interface State {
  isValid: boolean;
  validation: {
    portfolioName: Validation;
  };
  formData: {
    currency: string;
    portfolioName: string;
  };
}

@track({
  type: 'track',
})
class EditPortfolioModal extends Component<EditPortfolioModalProps, State> {
  private input: RefObject<HTMLInputElement>;

  static defaultProps = {
    isUpdating: false,
  };

  state = {
    isValid: false,
    validation: {
      portfolioName: {
        error: '',
        dirty: false,
      },
    },
    formData: {
      currency: 'USD',
      portfolioName: '',
    },
  };

  constructor(props) {
    super(props);
    this.input = createRef<HTMLInputElement>();
  }

  componentDidMount() {
    const { presetState } = this;
    presetState();
  }

  componentDidUpdate(prevProps) {
    const { presetState } = this;
    const { editPortfolioModalStatus: prevEditPortfolioModalStatus } =
      prevProps;
    const { editPortfolioModalStatus } = this.props;
    if (
      editPortfolioModalStatus !== prevEditPortfolioModalStatus &&
      editPortfolioModalStatus === 'visible'
    ) {
      presetState();
    }
  }

  presetState = () => {
    const { currencyISO, portfolioName } = this.props;
    const {
      input: { current },
    } = this;
    current && current.focus();
    this.setState(({ validation, isValid, formData }) => {
      return {
        isValid,
        validation: {
          portfolioName: {
            ...validation.portfolioName,
            dirty: true,
          },
        },
        formData: {
          ...formData,
          currency: currencyISO || 'USD',
          portfolioName: portfolioName || '',
        },
      };
    });
  };

  handleFormValidation = (validation: { portfolioName: Validation }) => {
    const arr = Object.values(validation);
    return (
      arr.filter((n) => n.dirty).length === arr.length &&
      arr.filter((n) => n.error.length).length === 0
    );
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { handleFormValidation } = this;
    const { formData } = this.state;
    const {
      portfolioId,
      portfolioName,
      onSubmit,
      portfolioKeys,
      portfolioEntities,
    } = this.props;

    const error = validateExistingPortfolioName(
      formData.portfolioName,
      portfolioName,
      portfolioKeys,
      portfolioEntities
    );

    const validation = {
      portfolioName: {
        error: error,
        dirty: true,
      },
    };

    if (!handleFormValidation(validation)) {
      this.setState({
        isValid: handleFormValidation(validation),
        validation: validation,
      });
      return false;
    }

    onSubmit({
      currency: formData.currency,
      portfolioId,
      portfolioName: formData.portfolioName,
    });
  };

  handleBlur = (e) => {
    const { handleFormValidation } = this;
    const { portfolioKeys, portfolioEntities, portfolioName } = this.props;
    const {
      target: { value, name },
    } = e;
    const { validation, isValid } = this.state;
    const error = validateExistingPortfolioName(
      value,
      portfolioName,
      portfolioKeys,
      portfolioEntities
    );

    const obj = {
      ...validation,
      [name]: {
        error: error,
        dirty: true,
      },
    };
    if (!validation[name].dirty || handleFormValidation(obj) !== isValid) {
      this.setState({
        isValid: handleFormValidation(obj),
        validation: obj,
      });
    }
  };

  handleChange = (e) => {
    const {
      target: { value, name },
    } = e;
    const { handleFormValidation } = this;
    const { portfolioKeys, portfolioEntities, portfolioName } = this.props;

    this.setState(({ validation, isValid, formData }) => {
      const error = validateExistingPortfolioName(
        value,
        portfolioName,
        portfolioKeys,
        portfolioEntities
      );
      if (validation[name].dirty || error.length === 0) {
        const obj = {
          ...validation,
          [name]: {
            ...(error.length === 0 ? { dirty: true } : validation[name]),
            error: error,
          },
        };
        if (
          validation[name].error !== error ||
          handleFormValidation(obj) !== isValid
        ) {
          return {
            isValid: handleFormValidation(obj),
            validation: obj,
            formData: {
              ...formData,
              [name]: value,
            },
          };
        } else {
          return {
            isValid,
            validation,
            formData: {
              ...formData,
              [name]: value,
            },
          };
        }
      }
      return {
        isValid,
        validation,
        formData: {
          ...formData,
          [name]: value,
        },
      };
    });
  };

  handleSelect = (value) => {
    const { handleFormValidation } = this;
    this.setState(({ validation, formData }) => {
      return {
        isValid: handleFormValidation(validation),
        validation,
        formData: {
          ...formData,
          currency: value,
        },
      };
    });
  };

  render() {
    const { input, handleBlur, handleChange, handleSubmit, handleSelect } =
      this;
    const { validation, formData } = this.state;
    const { error, isUpdating, onClose, editPortfolioModalStatus } = this.props;

    return editPortfolioModalStatus === 'visible' ? (
      <Modal
        title="Edit Portfolio"
        scrollable={false}
        asForm={true}
        disabled={isUpdating}
        primaryLabel="Save"
        secondaryLabel="Cancel"
        onSecondaryClick={onClose}
        onPrimaryClick={handleSubmit as () => void}
        primaryLoading={isUpdating}
      >
        <>
          {error && error.length > 0 && !isUpdating && (
            <ServerNotification alert small>
              {error}
            </ServerNotification>
          )}
          <FieldWrapper>
            <Textfield
              name="portfolioName"
              type="text"
              ref={input}
              styleType="standardReverse"
              label="Portfolio Name"
              placeholder="e.g. My New Portfolio"
              disabled={isUpdating}
              onBlur={handleBlur}
              onChange={handleChange}
              error={validation.portfolioName.error}
              value={formData.portfolioName}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Dropdown
              styleType="standardReverse"
              name="portfolioCurrency"
              label="Portfolio Currency"
              options={supportedCurrencies}
              value={formData.currency}
              onSelect={handleSelect}
            />
          </FieldWrapper>
        </>
      </Modal>
    ) : null;
  }
}

export default EditPortfolioModal;
